/**
 * Formats a price with currency symbol
 * @param price - The price value
 * @param currency - Currency code (e.g., "USD")
 * @returns Formatted price string (e.g., "$48,234")
 */
export const formatPrice = (price: number, currency: string): string => {
  const symbol = currency === 'USD' ? '$' : currency;
  return `${symbol}${price.toLocaleString('en-US')}`;
};

/**
 * Formats a price change with sign and currency symbol
 * @param change - The change value
 * @param currency - Currency code (e.g., "USD")
 * @returns Formatted change string (e.g., "+$2,384")
 */
export const formatPriceChange = (change: number, currency: string): string => {
  const symbol = currency === 'USD' ? '$' : currency;
  const sign = change >= 0 ? '+' : '';
  return `${sign}${symbol}${Math.abs(change).toLocaleString('en-US')}`;
};

/**
 * Formats a percentage change with sign
 * @param percent - The percentage value
 * @returns Formatted percentage string (e.g., "+5.2%")
 */
export const formatPercentChange = (percent: number): string => {
  const sign = percent >= 0 ? '+' : '';
  return `${sign}${percent.toFixed(1)}%`;
};

/**
 * Formats large numbers to abbreviated form
 * @param value - The numeric value
 * @returns Formatted string (e.g., "945B", "19.6M")
 */
export const formatMarketCap = (value: number): string => {
  if (value >= 1e12) {
    return `$${(value / 1e12).toFixed(1)}T`;
  }
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(0)}B`;
  }
  if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(1)}M`;
  }
  return `$${value.toLocaleString('en-US')}`;
};

/**
 * Formats supply with unit
 * @param value - The supply value
 * @param unit - The unit (e.g., "BTC")
 * @returns Formatted string (e.g., "19.6M BTC")
 */
export const formatSupply = (value: number, unit: string): string => {
  if (value >= 1e9) {
    return `${(value / 1e9).toFixed(1)}B ${unit}`;
  }
  if (value >= 1e6) {
    return `${(value / 1e6).toFixed(1)}M ${unit}`;
  }
  if (value >= 1e3) {
    return `${(value / 1e3).toFixed(1)}K ${unit}`;
  }
  return `${value.toLocaleString('en-US')} ${unit}`;
};

/**
 * Determines color based on price change direction
 * @param change - The price change value
 * @returns Color type ('success' for positive, 'error' for negative)
 */
export const getPriceChangeColor = (change: number): 'success' | 'error' => {
  return change >= 0 ? 'success' : 'error';
};

/**
 * Gets color configuration for volume tag
 * @param tag - The volume tag type
 * @returns Color object with border and background colors
 */
export const getVolumeTagColor = (
  tag: string,
): { borderColor: string; backgroundColor: string } => {
  switch (tag) {
    case 'pinnedAssetOverview.tags.longVolume':
      return {
        borderColor: 'warning.main', // Orange
        backgroundColor: 'rgba(255, 123, 48, 0.1)',
      };
    case 'pinnedAssetOverview.tags.shortVolume':
      return {
        borderColor: 'info.main', // Blue
        backgroundColor: 'rgba(75, 132, 254, 0.1)',
      };
    case 'pinnedAssetOverview.tags.neutralVolume':
    default:
      return {
        borderColor: 'divider',
        backgroundColor: 'rgba(159, 161, 164, 0.1)',
      };
  }
};

/**
 * Gets color configuration for position tag
 * @param tag - The position tag type
 * @returns Color object with border and background colors
 */
export const getPositionTagColor = (
  tag: string,
): { borderColor: string; backgroundColor: string } => {
  switch (tag) {
    case 'pinnedAssetOverview.tags.strongLong':
      return {
        borderColor: 'success.main',
        backgroundColor: 'rgba(13, 231, 126, 0.1)',
      };
    case 'pinnedAssetOverview.tags.long':
      return {
        borderColor: 'warning.main',
        backgroundColor: 'rgba(255, 123, 48, 0.1)',
      };
    case 'pinnedAssetOverview.tags.strongShort':
      return {
        borderColor: '#4B84FE',
        backgroundColor: 'rgba(75, 132, 254, 0.1)',
      };
    case 'pinnedAssetOverview.tags.short':
      return {
        borderColor: 'error.main',
        backgroundColor: 'rgba(254, 75, 75, 0.1)',
      };
    case 'pinnedAssetOverview.tags.neutralPositions':
    default:
      return {
        borderColor: 'text.third',
        backgroundColor: 'text.fifth',
      };
  }
};

/**
 * Calculates bar widths for sentiment visualization
 * Ensures percentages sum to 100%
 * @param assetPercent - Asset sentiment percentage
 * @param marketPercent - Market sentiment percentage
 * @returns Object with calculated widths
 */
export const calculateSentimentBarWidths = (
  assetPercent: number,
  marketPercent: number,
): { asset: number; market: number } => {
  const total = assetPercent + marketPercent;
  if (total === 0) {
    return { asset: 50, market: 50 };
  }
  return {
    asset: (assetPercent / total) * 100,
    market: (marketPercent / total) * 100,
  };
};
